/**
 * cy-sso helpers for Epicenter staff/mentor flows.
 *
 * Replaces the old EpicenterApi `/commonAuth/token` (mentor sandbox) and
 * `/impersonateUser/create` (admin impersonation) flows. Both now exchange the
 * logged-in Epicenter access token for cy-sso tokens, then hand those tokens to
 * the target app's `#/sso-login` landing route via the shared `.codeyoung.com`
 * cookie bridge (with an encrypted URL-hash fallback for localhost dev).
 *
 * Transport mirrors sandbox/StudentPortal's existing bridge:
 *   - StudentPortal consumes `_cy_auth_bridge` (JSON) or `_spb` (encrypted hash).
 *   - Sandbox consumes shared `Authorization` + `refresh-token` cookies, or the
 *     `_sbx` encrypted hash param read by its sso-login route.
 */
import axios from "axios";
import Cookies from "js-cookie";
import { decrypt, encrypt } from "./others";

const trimSlash = (url) => (url || "").replace(/\/+$/, "");

const SSO_BASE_URL = trimSlash(process.env.REACT_APP_SSO_BASE_URL || "https://cy-sso.codeyoung.com");
const SANDBOX_URL = trimSlash(process.env.REACT_APP_SANDBOX_URL || "https://sandbox.codeyoung.com");
const SP_FRONTEND_URL = trimSlash(process.env.REACT_APP_SP_FRONTEND_URL || "https://studentportal.codeyoung.com");

const BRIDGE_COOKIE_NAME = "_cy_auth_bridge";
const BRIDGE_TTL_SECONDS = 30;

// The sandbox-handoff auth cookies are ONE-SHOT transport for the target app's
// /sso-login route — never an ongoing session store (the receiver deletes them
// after consuming; see Sandbox SSOLanding). Keep them short-lived so a copy the
// receiver missed can't linger, go stale after the first token rotation, and
// get (re-)adopted later — that stale re-adoption is what silently logged
// mentors out of Sandbox ~1h after a one-click handoff.
const HANDOFF_TTL_SECONDS = 60;

/**
 * Cookie Domain for the cross-app handoff, derived from the CURRENT host so an
 * environment's write stays in that environment:
 *   epicenter.codeyoung.com     → .codeyoung.com
 *   epicenter.dev.codeyoung.com → .dev.codeyoung.com   (never leaks to prod)
 * Non-codeyoung hosts fall back to the legacy hardcoded prod domain (where the
 * browser rejects the cross-site write exactly as it always did).
 */
const getSharedCookieDomain = () => {
  const host = (typeof window !== "undefined" && window.location.hostname) || "";
  if (!host.endsWith("codeyoung.com")) return ".codeyoung.com";
  const labels = host.split(".");
  if (labels.length >= 3) return "." + labels.slice(1).join(".");
  return "." + host;
};

const isLocalhost = () => typeof window !== "undefined" && window.location.hostname === "localhost";

const getEpicenterTokens = () => {
  try {
    const user = decrypt(localStorage.getItem("user"));
    return {
      accessToken: (user && user.ACCESS_TOKEN) || null,
      refreshToken: (user && user.REFRESH_TOKEN) || null,
    };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
};

const openTarget = (url, newTab) => {
  if (newTab) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.href = url;
  }
};

/**
 * Mentor one-click sandbox login.
 * @param {Object} [opts]
 * @param {string} [opts.redirect="/home"] Sandbox hash route to land on after auth.
 * @param {boolean} [opts.newTab=true]
 */
export const openSandboxAsMentor = async ({ redirect = "/home", newTab = true } = {}) => {
  const { accessToken, refreshToken } = getEpicenterTokens();
  if (!accessToken && !refreshToken) throw new Error("Not authenticated");

  const { data } = await axios.post(`${SSO_BASE_URL}/api/login/sandbox/epicenter-auth`, {
    epicenterAccessToken: accessToken,
    epicenterRefreshToken: refreshToken,
  });
  if (!data || !data.valid || !data.tokens) {
    throw new Error((data && data.msg) || "Failed to get sandbox auth");
  }

  const cyAccessToken = data.tokens.accessToken;
  const cyRefreshToken = data.tokens.refreshToken;
  let target = `${SANDBOX_URL}/#/sso-login?redirect=${encodeURIComponent(redirect)}`;

  if (!isLocalhost()) {
    const handoffOpts = {
      path: "/",
      domain: getSharedCookieDomain(),
      expires: HANDOFF_TTL_SECONDS / 86400, // js-cookie expires is in days
      sameSite: "Lax",
      secure: true,
    };
    Cookies.set("Authorization", cyAccessToken, handoffOpts);
    Cookies.set("refresh-token", cyRefreshToken, handoffOpts);
  } else {
    // localhost dev: cross-port cookies don't share a domain — pass via hash.
    const payload = encrypt({ ACCESS_TOKEN: cyAccessToken, REFRESH_TOKEN: cyRefreshToken, ts: Date.now() });
    target += `&_sbx=${encodeURIComponent(payload)}`;
  }
  openTarget(target, newTab);
};

/**
 * Exchange the Epicenter token for a cy-sso STUDENT token (impersonation).
 * Returns the cy-sso response { valid, tokens, studentId, studentEmail, studentName }.
 */
export const getImpersonationAuth = async (studentId) => {
  const { accessToken, refreshToken } = getEpicenterTokens();
  if (!accessToken && !refreshToken) throw new Error("Not authenticated");

  const { data } = await axios.post(`${SSO_BASE_URL}/api/login/student/impersonate-auth`, {
    epicenterAccessToken: accessToken,
    epicenterRefreshToken: refreshToken,
    studentId,
  });
  if (!data || !data.valid || !data.tokens) {
    throw new Error((data && data.msg) || "Failed to get impersonation auth");
  }
  return data;
};

/**
 * Open StudentPortal logged in as the impersonated student, using the auth
 * returned by getImpersonationAuth. Builds the SP-shaped bridge payload that
 * StudentPortal's consumeAuthBridge expects.
 */
export const openStudentPortalAsImpersonatedUser = (authData, { newTab = true } = {}) => {
  const { tokens, studentId, studentEmail, studentName } = authData || {};
  if (!tokens || !tokens.accessToken) throw new Error("Missing impersonation tokens");

  const user = {
    ACCESS_TOKEN: tokens.accessToken,
    REFRESH_TOKEN: tokens.refreshToken,
    msg: "Log-in successful",
    studentName: studentName || "",
    studentId: studentId || "",
    studentEmail: studentEmail || "",
    profilePicUrl: "",
    source: "Codeyoung",
    Authorization: tokens.accessToken,
    "refresh-token": tokens.refreshToken,
    role: "student",
    user_id: studentId || "",
    isPaid: false,
  };
  const bridgePayload = { user, ts: Date.now(), nonce: Math.random().toString(36).slice(2, 10) };
  const target = `${SP_FRONTEND_URL}/#/sso-login`;

  if (!isLocalhost()) {
    Cookies.set(BRIDGE_COOKIE_NAME, JSON.stringify(bridgePayload), {
      path: "/",
      domain: getSharedCookieDomain(),
      expires: BRIDGE_TTL_SECONDS / 86400, // js-cookie expires in days
      sameSite: "Lax",
      secure: true,
    });
    openTarget(target, newTab);
  } else {
    const safe = encodeURIComponent(encrypt(bridgePayload));
    openTarget(`${target}?_spb=${safe}`, newTab);
  }
};
